'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';

import type { ConversationWithDetails } from '../types';

/**
 * Hook to mark a conversation as read
 */
export function useMarkAsRead() {
  const client = useSupabase();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (conversationId: string) => {
      const { error } = await client.rpc('mark_conversation_read', {
        p_conversation_id: conversationId,
      });

      if (error) {
        throw new Error(error.message);
      }

      return { conversationId };
    },

    onMutate: async (conversationId) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({
        queryKey: ['chat-conversations'],
      });
      await queryClient.cancelQueries({
        queryKey: ['chat-total-unread-count'],
      });

      // Snapshot for rollback
      const previousConversations = queryClient.getQueryData([
        'chat-conversations',
      ]);
      const previousUnreadCount = queryClient.getQueryData([
        'chat-total-unread-count',
      ]);

      // Optimistically update conversations list
      queryClient.setQueryData<ConversationWithDetails[]>(
        ['chat-conversations'],
        (oldData) => {
          if (!oldData) return oldData;

          return oldData.map((conv) => {
            if (conv.id === conversationId && conv.my_participation) {
              return {
                ...conv,
                my_participation: {
                  ...conv.my_participation,
                  unread_count: 0,
                  last_read_at: new Date().toISOString(),
                },
              };
            }
            return conv;
          });
        },
      );

      // Optimistically update total unread count
      queryClient.setQueryData<number>(
        ['chat-total-unread-count'],
        (oldCount) => {
          if (typeof oldCount !== 'number') return oldCount;

          // Find the conversation's unread count
          const conversations = queryClient.getQueryData<
            ConversationWithDetails[]
          >(['chat-conversations']);
          const conv = conversations?.find((c) => c.id === conversationId);
          const unreadToSubtract = conv?.my_participation?.unread_count || 0;

          return Math.max(0, oldCount - unreadToSubtract);
        },
      );

      return { previousConversations, previousUnreadCount, conversationId };
    },

    onError: (_error, _conversationId, context) => {
      // Rollback on error
      if (context?.previousConversations) {
        queryClient.setQueryData(
          ['chat-conversations'],
          context.previousConversations,
        );
      }
      if (context?.previousUnreadCount !== undefined) {
        queryClient.setQueryData(
          ['chat-total-unread-count'],
          context.previousUnreadCount,
        );
      }
    },

    onSuccess: (_data, conversationId) => {
      // Invalidate to ensure sync with server
      queryClient.invalidateQueries({
        queryKey: ['chat-conversations'],
      });
      queryClient.invalidateQueries({
        queryKey: ['chat-conversation', conversationId],
      });
      queryClient.invalidateQueries({
        queryKey: ['chat-total-unread-count'],
      });
    },
  });

  // Use a ref to make the callback stable (mutation.mutateAsync may change)
  const mutateRef = useRef(mutation.mutateAsync);
  useEffect(() => {
    mutateRef.current = mutation.mutateAsync;
  });

  const markAsRead = useCallback((conversationId: string) => {
    return mutateRef.current(conversationId);
  }, []);

  return {
    markAsRead,
    isLoading: mutation.isPending,
    error: mutation.error,
  };
}

/**
 * Hook to mark all conversations as read
 */
export function useMarkAllAsRead() {
  const client = useSupabase();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async () => {
      const {
        data: { user },
      } = await client.auth.getUser();
      if (!user) {
        throw new Error('Not authenticated');
      }

      // Update all participant records for this user
      const { error } = await client
        .from('conversation_participants')
        .update({
          unread_count: 0,
          last_read_at: new Date().toISOString(),
        })
        .eq('user_id', user.id)
        .eq('is_active', true)
        .gt('unread_count', 0);

      if (error) {
        throw new Error(error.message);
      }

      return { success: true };
    },

    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['chat-conversations'] });
      await queryClient.cancelQueries({
        queryKey: ['chat-total-unread-count'],
      });

      const previousConversations = queryClient.getQueryData([
        'chat-conversations',
      ]);
      const previousUnreadCount = queryClient.getQueryData([
        'chat-total-unread-count',
      ]);

      // Optimistically mark all as read
      queryClient.setQueryData<ConversationWithDetails[]>(
        ['chat-conversations'],
        (oldData) => {
          if (!oldData) return oldData;

          return oldData.map((conv) => ({
            ...conv,
            my_participation: conv.my_participation
              ? {
                  ...conv.my_participation,
                  unread_count: 0,
                  last_read_at: new Date().toISOString(),
                }
              : null,
          }));
        },
      );

      queryClient.setQueryData(['chat-total-unread-count'], 0);

      return { previousConversations, previousUnreadCount };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousConversations) {
        queryClient.setQueryData(
          ['chat-conversations'],
          context.previousConversations,
        );
      }
      if (context?.previousUnreadCount !== undefined) {
        queryClient.setQueryData(
          ['chat-total-unread-count'],
          context.previousUnreadCount,
        );
      }
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-conversations'] });
      queryClient.invalidateQueries({ queryKey: ['chat-total-unread-count'] });
    },
  });

  return {
    markAllAsRead: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
  };
}
